/**
 * TanStack Query hooks for the workflows API.
 *
 * Backend: `apps/api/flow_api/routers/workflows.py`. Read routes are
 * VIEWER+; training-mode toggle is SUPERVISOR+ (same as promote).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/apiClient";

export interface Workflow {
  id: string;
  name: string;
  description: string;
  entityCode: string;
  workflowType: string;
  runnerTier: string;
  maturityState: string;
  breakInTarget: number;
  breakInSuccessfulRuns: number;
  concurrencyCap: number;
  currentVersion: number;
  trainingMode: boolean;
  createdAt: string | null;
  activatedAt: string | null;
}

export interface WorkflowsList {
  items: Workflow[];
  total: number;
}

const QK = {
  list: (entity?: string, state?: string) =>
    ["workflows", "list", entity ?? "all", state ?? "all"] as const,
  single: (id: string) => ["workflows", "single", id] as const,
};

export function useWorkflows(params?: { entity?: string; maturityState?: string }) {
  return useQuery<WorkflowsList>({
    queryKey: QK.list(params?.entity, params?.maturityState),
    queryFn: () => {
      const qs = new URLSearchParams();
      if (params?.entity) qs.set("entity", params.entity);
      if (params?.maturityState) qs.set("maturity_state", params.maturityState);
      const tail = qs.toString() ? `?${qs.toString()}` : "";
      return apiFetch(`/workflows${tail}`);
    },
  });
}

export function useSetTrainingMode(id: string) {
  const qc = useQueryClient();
  return useMutation<Workflow, Error, boolean>({
    mutationFn: (enabled) =>
      apiFetch(`/workflows/${id}/training-mode`, {
        method: "PUT",
        body: JSON.stringify({ enabled }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["workflows", "list"] });
      qc.invalidateQueries({ queryKey: QK.single(id) });
    },
  });
}


export interface QueueJobPayload {
  workflow_id: string;
  entity_code: string;
  inputs?: Record<string, unknown>;
  priority_tier?: "urgent" | "scheduled";
  test_mode?: boolean;
  mode?: "smoke" | "full";
}

export interface QueuedJob {
  id: string;
  status: string;
  entity: string;
}

export function useQueueJob() {
  const qc = useQueryClient();
  return useMutation<QueuedJob, Error, QueueJobPayload>({
    mutationFn: (payload) =>
      apiFetch("/jobs", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["jobs"] });
    },
  });
}


/**
 * Composed action for the "Train this workflow" launcher:
 * 1. Flip training mode on (if caller opts in) — supervisors typically do.
 * 2. Queue the job in `full` mode + `test_mode=true` so the runner does
 *    real LLM work but the job is flagged as a training run for the
 *    dashboard.
 * The caller is responsible for navigating to `/jobs/:id/replay`.
 */
export async function startTrainingRun(args: {
  workflow: Workflow;
  inputs: Record<string, unknown>;
  enableTrainingMode: boolean;
}): Promise<QueuedJob> {
  if (args.enableTrainingMode && !args.workflow.trainingMode) {
    await apiFetch(`/workflows/${args.workflow.id}/training-mode`, {
      method: "PUT",
      body: JSON.stringify({ enabled: true }),
    });
  }
  return apiFetch<QueuedJob>("/jobs", {
    method: "POST",
    body: JSON.stringify({
      workflow_id: args.workflow.id,
      entity_code: args.workflow.entityCode,
      inputs: args.inputs,
      priority_tier: "urgent",
      test_mode: true,
      mode: "full",
    }),
  });
}
